import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Timer, Zap, Trophy, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpeedQuizPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameState, setGameState] = useState('loading'); // loading, playing, finished
  const [answers, setAnswers] = useState([]);

  // Fetch questions
  const fetchQuestions = useCallback(async () => {
    setGameState('loading');
    try {
      const response = await axios.get(`${API_URL}/api/games/speed-quiz`);
      setQuestions(response.data.questions);
      setTimeLeft(response.data.time_limit || 30);
    } catch (error) {
      // Fallback questions
      setQuestions([
        { question: "Como se diz 'Obrigado' em inglês?", options: ["Please", "Sorry", "Thank you", "Hello"], correct: 2 },
        { question: "'Good morning' significa:", options: ["Boa noite", "Bom dia", "Boa tarde", "Até logo"], correct: 1 },
        { question: "Como pedir ajuda em inglês?", options: ["Help me", "Stop", "Go away", "Wait"], correct: 0 },
        { question: "'I don't understand' significa:", options: ["Eu entendo", "Não sei", "Não entendo", "Talvez"], correct: 2 },
        { question: "Como perguntar 'Quanto custa?'", options: ["What time?", "How much?", "Where is?", "Who is?"], correct: 1 },
      ]);
      setTimeLeft(30);
    } finally {
      setCurrentIndex(0);
      setScore(0);
      setSelectedAnswer(null);
      setAnswers([]);
      setGameState('playing');
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing' || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameState('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  const handleAnswer = (answerIndex) => {
    if (selectedAnswer !== null || gameState !== 'playing') return;

    setSelectedAnswer(answerIndex);
    const isCorrect = answerIndex === questions[currentIndex].correct;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    setAnswers(prev => [...prev, {
      question: questions[currentIndex].question,
      selected: answerIndex,
      correct: questions[currentIndex].correct,
      isCorrect
    }]);

    // Move to next question after delay
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setSelectedAnswer(null);
      } else {
        setGameState('finished');
      }
    }, 800);
  };

  const calculateXP = () => {
    // XP based on score and time remaining
    const baseXP = score * 20;
    const timeBonus = Math.floor(timeLeft * 2);
    return baseXP + timeBonus;
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  // Loading state
  if (gameState === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando quiz...</p>
        </div>
      </div>
    );
  }

  // Finished state
  if (gameState === 'finished') {
    const accuracy = Math.round((score / questions.length) * 100);
    const xpEarned = calculateXP();

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="card-elevated p-6 max-w-sm w-full text-center space-y-4 animate-scale-in">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center mx-auto',
            accuracy >= 80 ? 'bg-success/10' : accuracy >= 50 ? 'bg-warning/10' : 'bg-destructive/10'
          )}>
            <Trophy className={cn(
              'w-10 h-10',
              accuracy >= 80 ? 'text-success' : accuracy >= 50 ? 'text-warning' : 'text-destructive'
            )} />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold">
              {accuracy >= 80 ? 'Excelente! 🎉' : accuracy >= 50 ? 'Bom trabalho! 👍' : 'Continue praticando! 💪'}
            </h2>
            <p className="text-muted-foreground">Quiz finalizado</p>
          </div>

          <div className="grid grid-cols-3 gap-4 py-4">
            <div className="text-center">
              <p className="text-xl font-bold text-primary">{score}/{questions.length}</p>
              <p className="text-xs text-muted-foreground">Acertos</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-accent">{accuracy}%</p>
              <p className="text-xs text-muted-foreground">Precisão</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-success">+{xpEarned}</p>
              <p className="text-xs text-muted-foreground">XP</p>
            </div>
          </div>

          {/* Answer Review */}
          <div className="text-left space-y-2 max-h-40 overflow-y-auto">
            {answers.map((answer, index) => (
              <div key={index} className={cn(
                'flex items-center gap-2 p-2 rounded-lg text-xs',
                answer.isCorrect ? 'bg-success/10' : 'bg-destructive/10'
              )}>
                {answer.isCorrect ? (
                  <Check className="w-4 h-4 text-success shrink-0" />
                ) : (
                  <X className="w-4 h-4 text-destructive shrink-0" />
                )}
                <span className="truncate">{answer.question}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
            <Button
              className="flex-1 btn-primary-gradient"
              onClick={fetchQuestions}
            >
              Jogar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Playing state
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-primary">
              <Zap className="w-4 h-4" />
              <span className="font-semibold">{score}</span>
            </div>
            <div className={cn(
              'flex items-center gap-1.5 px-3 py-1 rounded-full',
              timeLeft <= 10 ? 'bg-destructive/10 text-destructive animate-pulse' : 'bg-muted text-muted-foreground'
            )}>
              <Timer className="w-4 h-4" />
              <span className="font-semibold">{timeLeft}s</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="px-4 py-2">
        <Progress value={progress} className="h-1.5" />
        <p className="text-xs text-muted-foreground text-center mt-1">
          Pergunta {currentIndex + 1} de {questions.length}
        </p>
      </div>

      {/* Question */}
      <main className="flex-1 flex items-center p-4">
        <div className="max-w-lg mx-auto w-full space-y-6">
          {/* Question Text */}
          <div className="text-center py-6">
            <h2 className="font-display text-xl font-semibold">
              {currentQuestion?.question}
            </h2>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion?.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === currentQuestion.correct;
              const showResult = selectedAnswer !== null;

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={selectedAnswer !== null}
                  className={cn(
                    'w-full p-4 rounded-xl border-2 text-left transition-all',
                    showResult
                      ? isCorrect
                        ? 'border-success bg-success/10 text-success'
                        : isSelected
                          ? 'border-destructive bg-destructive/10 text-destructive'
                          : 'border-border bg-card text-muted-foreground'
                      : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                      showResult
                        ? isCorrect
                          ? 'bg-success text-success-foreground'
                          : isSelected
                            ? 'bg-destructive text-destructive-foreground'
                            : 'bg-muted text-muted-foreground'
                        : 'bg-muted text-muted-foreground'
                    )}>
                      {showResult && isCorrect ? (
                        <Check className="w-4 h-4" />
                      ) : showResult && isSelected ? (
                        <X className="w-4 h-4" />
                      ) : (
                        String.fromCharCode(65 + index)
                      )}
                    </div>
                    <span className="font-medium">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SpeedQuizPage;
